<template>
    <div
        class="MatchingSelectionReviewBase"
        :class="{ 'MatchingSelectionReviewBase--globalScope': showGlobalDocuments }"
    >
        <ConfirmationModal
            v-if="showConfirmationModal"
            additional-class-name="MatchingSelectionReviewBase__confirmationModal"
            title="Save Action Review"
            @input="onModalAction"
        >
            Future global actions will be automatically reviewed based on your review. Saving this action cannot be
            undone.
        </ConfirmationModal>
        <div class="MatchingSelectionReviewBase__container">
            <div class="MatchingSelectionReviewBase__form">
                <Table class="MatchingSelectionReviewBase__table">
                    <template #head>
                        <HeadCell class="MatchingSelectionReviewBase__fieldHeader">
                            Field
                        </HeadCell>
                        <HeadCell class="MatchingSelectionReviewBase__valueHeader">
                            Value
                        </HeadCell>
                    </template>
                    <Row hoverable>
                        <Cell
                            data-test-id="MatchingSelectionReviewBase__providedFieldHeader"
                            class="MatchingSelectionReviewBase__fieldHeader"
                        >
                            {{ providedSubjectTitle }}
                        </Cell>
                        <Cell
                            data-test-id="MatchingSelectionReviewBase__providedValueHeader"
                            class="MatchingSelectionReviewBase__valueHeader"
                        >
                            <span v-if="provided">
                                {{ provided }}
                            </span>
                            <EmptyValueDashSymbol v-else />
                        </Cell>
                    </Row>
                    <Row hoverable>
                        <Cell
                            data-test-id="MatchingSelectionReviewBase__matchingFieldHeader"
                            class="MatchingSelectionReviewBase__fieldHeader"
                        >
                            {{ matchingSubjectTitle }}
                        </Cell>
                        <Cell
                            data-test-id="MatchingSelectionReviewBase__matchingValueHeader"
                            class="MatchingSelectionReviewBase__valueHeader"
                        >
                            <!-- A decided action shows what was chosen in place of the picker. -->
                            <span v-if="readOnly" class="MatchingSelectionReviewBase__recordedSelection">
                                {{ recordedSelection }}
                            </span>
                            <template v-else>
                                <Dropdown
                                    :placeholder="dropdownPlaceholder"
                                    :options="options"
                                    :selected="selectedOptions"
                                    :disabled="matchingNotListed"
                                    @input="onMatchingSelection"
                                />
                                <Checkbox
                                    class="MatchingSelectionReviewBase__matchingNotListed"
                                    @update:model-value="setMatchingNotFoundStatus"
                                >
                                    {{ noMatchesText }}
                                </Checkbox>
                            </template>
                        </Cell>
                    </Row>
                </Table>
                <RbacDisableDefaultGuard v-if="!readOnly" :features="[RbacFeature.actionsResolveGlobalAction]">
                    <NewButton
                        class="MatchingSelectionReviewBase__saveButton"
                        type="primary"
                        :disabled="saveDisabled"
                        @click="onSave"
                    >
                        Save
                    </NewButton>
                </RbacDisableDefaultGuard>
            </div>

            <RbacDisplayGuard v-if="showGlobalDocuments" :features="[RbacFeature.actionsViewDocument]">
                <div class="MatchingSelectionReviewBase__documents">
                    <GlobalActionDocumentPanel :action-review="actionReview" />
                </div>
            </RbacDisplayGuard>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { Checkbox } from '@evidentid/dashboard-commons/components/Checkbox';
    import { Dropdown } from '@evidentid/dashboard-commons/components/Dropdown';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import { Cell, HeadCell, Row, Table } from '@evidentid/dashboard-commons/components/Table';
    import RbacDisableDefaultGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableDefaultGuard.vue';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { ActionReview } from '@evidentid/tprm-portal-lib/models/entity-actions-review';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import EmptyValueDashSymbol
        from '@/modules/entity-actions-review/components/EmptyValueDashSymbol/EmptyValueDashSymbol.vue';
    import GlobalActionDocumentPanel
        from '@/modules/entity-actions-review/components/GlobalActionDocumentPanel/GlobalActionDocumentPanel.vue';
    import { getAffectedEntityCount } from '@/modules/entity-actions-review/utils/globalActionScope';

    @Component({
        emits: [ 'resolve' ],
        components: {
            RbacDisableDefaultGuard,
            RbacDisplayGuard,
            ConfirmationModal,
            EmptyValueDashSymbol,
            NewButton,
            Checkbox,
            Dropdown,
            GlobalActionDocumentPanel,
            HeadCell,
            Table,
            Cell,
            Row,
        },
    })
    export default class MatchingSelectionReviewBase extends Vue {
        @Prop({ type: Object, default: () => ({}) })
        private actionReview!: ActionReview;

        @Prop({ type: Boolean, default: false })
        private readOnly!: boolean;

        /** What was chosen when this action was decided, read from the stored resolve payload.
         *
         * "Nothing was recorded" and "no match was chosen" are different answers and must not collapse
         * into each other: actions decided before resolutions were stored have no payload at all, and
         * reporting those as an explicit no-match asserts a decision nobody made.
         */
        private get recordedSelection(): string {
            const resolution = this.actionReview.resolution;
            if (!resolution || resolution.result == null) {
                return 'Not recorded';
            }
            const result = resolution.result as Record<string, unknown>;
            const chosen = result.category;
            return typeof chosen === 'string' && chosen ? chosen : this.noMatchesText;
        }

        @Prop({ type: Array, default: () => [] })
        private matchingList!: string[];

        @Prop({ type: String, default: '' })
        private provided!: string;

        @Prop({ type: String, default: 'Provided' })
        private providedSubjectTitle!: string;

        @Prop({ type: String, default: 'Match' })
        private matchingSubjectTitle!: string;

        @Prop({ type: String, default: 'No Match' })
        private noMatchesText!: string;

        private RbacFeature = RbacFeature;
        private matchingNotListed: boolean = false;
        private selectedOptions: DropdownOption[] = [];
        private options: DropdownOption[] = [];
        private showConfirmationModal: boolean = false;

        private get showGlobalDocuments(): boolean {
            return !this.readOnly && getAffectedEntityCount(this.actionReview.evaluationResults) > 0;
        }

        private get saveDisabled(): boolean {
            return this.selectedOptions.length === 0 && !this.matchingNotListed;
        }

        private get dropdownPlaceholder(): string {
            return this.matchingNotListed ? 'N/A' : `Select ${this.matchingSubjectTitle}`;
        }

        @Watch('matchingList', { immediate: true })
        private onMatchingListChange(): void {
            this.options = [ ...this.matchingList ].sort().map((x) => ({ label: x, value: x }));
        }

        private setMatchingNotFoundStatus(checked: boolean): void {
            this.matchingNotListed = checked;
            if (checked) {
                this.selectedOptions = [];
            }
        }

        private onMatchingSelection(option: DropdownOption): void {
            this.selectedOptions = [ option ];
        }

        private onSave(): void {
            this.showConfirmationModal = true;
        }

        private onModalAction(confirmed: boolean): void {
            if (confirmed) {
                this.$emit('resolve',
                           this.selectedOptions.length === 0 ? null : this.selectedOptions[0].value,
                );
            }
            this.showConfirmationModal = false;
        }
    }
</script>
